import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { QUOTE_URL } from '../lib/links';

const Navbar = () => {
  const { language, t, changeLanguage } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  const scrollToNetwork = (mode) => {
    const element = document.getElementById('warehouses');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      window.dispatchEvent(new CustomEvent('setNetworkView', { detail: mode }));
      setIsMobileMenuOpen(false);
    }
  };

  const languages = ['EN', 'ES', 'PT', 'ZH'];

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-black/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="/" data-testid="logo-link" className="flex items-center gap-3 shrink-0">
              <img
                src="/logo.png"
                alt="MailAmericas"
                className="h-10 w-auto"
              />
            </a>

            {/* Desktop Navigation */}
            <div className="hidden xl:flex items-center gap-5">
              <button
                onClick={() => scrollToSection('process')}
                className="nav-link text-white/80 hover:text-white text-sm font-medium tracking-wide whitespace-nowrap"
                data-testid="nav-process"
              >
                {t('nav.process')}
              </button>
              <button
                onClick={() => scrollToSection('fulfillment')}
                className="nav-link text-white/80 hover:text-white text-sm font-medium tracking-wide whitespace-nowrap"
                data-testid="nav-fulfillment"
              >
                {t('nav.fulfillment')}
              </button>
              <button
                onClick={() => scrollToNetwork('origins')}
                className="nav-link text-white/80 hover:text-white text-sm font-medium tracking-wide whitespace-nowrap"
                data-testid="nav-warehouses"
              >
                {t('nav.warehouses')}
              </button>
              <button
                onClick={() => scrollToNetwork('destinations')}
                className="nav-link text-white/80 hover:text-white text-sm font-medium tracking-wide whitespace-nowrap"
                data-testid="nav-destinations"
              >
                {t('nav.destinations')}
              </button>
              <button
                onClick={() => scrollToSection('dangerous-goods')}
                className="nav-link text-white/80 hover:text-white text-sm font-medium tracking-wide whitespace-nowrap"
                data-testid="nav-dangerous-goods"
              >
                {t('nav.dangerousGoods')}
              </button>
              <button
                onClick={() => scrollToSection('returns')}
                className="nav-link text-white/80 hover:text-white text-sm font-medium tracking-wide whitespace-nowrap"
                data-testid="nav-returns"
              >
                {t('nav.returns')}
              </button>
              <button
                onClick={() => scrollToSection('api-docs')}
                className="nav-link text-white/80 hover:text-white text-sm font-medium tracking-wide whitespace-nowrap"
                data-testid="nav-api-docs"
              >
                {t('nav.apiDocs')}
              </button>
              <Link
                to="/account-setup"
                className="nav-link text-white/80 hover:text-white text-sm font-medium tracking-wide whitespace-nowrap"
                data-testid="nav-account-setup"
              >
                {t('nav.accountSetup')}
              </Link>
            </div>

            {/* Language Selector + Ship With Us Button */}
            <div className="hidden xl:flex items-center gap-4 shrink-0">
              <div className="flex items-center gap-1 border border-white/20 p-1">
                {languages.map((lang) => (
                  <button
                    key={lang}
                    onClick={() => changeLanguage(lang.toLowerCase())}
                    className={`lang-btn px-2 py-1.5 text-xs font-bold tracking-wider ${
                      language === lang.toLowerCase()
                        ? 'active'
                        : 'text-white/70 hover:text-white'
                    }`}
                    data-testid={`lang-${lang.toLowerCase()}`}
                  >
                    {lang}
                  </button>
                ))}
              </div>
              <a
                href={QUOTE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#7AC142] text-black px-4 py-2.5 text-sm font-bold uppercase tracking-wider whitespace-nowrap hover:bg-[#69A835] transition-colors"
                data-testid="ship-with-us-btn"
              >
                {t('nav.shipWithUs')}
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="xl:hidden text-white p-2"
              onClick={() => setIsMobileMenuOpen(true)}
              data-testid="mobile-menu-button"
              aria-label={t('a11y.openMenu')}
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed inset-0 bg-black z-[100] flex flex-col overflow-y-auto"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <img
                src="/logo.png"
                alt="MailAmericas"
                className="h-8 w-auto"
              />
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-white p-2"
                data-testid="close-mobile-menu"
                aria-label={t('a11y.closeMenu')}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex flex-col p-6 gap-6 flex-1">
              <button
                onClick={() => scrollToSection('process')}
                className="text-left text-white text-2xl font-bold tracking-tight hover:text-[#7AC142]"
              >
                {t('nav.process')}
              </button>
              <button
                onClick={() => scrollToSection('fulfillment')}
                className="text-left text-white text-2xl font-bold tracking-tight hover:text-[#7AC142]"
              >
                {t('nav.fulfillment')}
              </button>
              <button
                onClick={() => scrollToNetwork('origins')}
                className="text-left text-white text-2xl font-bold tracking-tight hover:text-[#7AC142]"
              >
                {t('nav.warehouses')}
              </button>
              <button
                onClick={() => scrollToNetwork('destinations')}
                className="text-left text-white text-2xl font-bold tracking-tight hover:text-[#7AC142]"
              >
                {t('nav.destinations')}
              </button>
              <button
                onClick={() => scrollToSection('dangerous-goods')}
                className="text-left text-white text-2xl font-bold tracking-tight hover:text-[#7AC142]"
              >
                {t('nav.dangerousGoods')}
              </button>
              <button
                onClick={() => scrollToSection('returns')}
                className="text-left text-white text-2xl font-bold tracking-tight hover:text-[#7AC142]"
              >
                {t('nav.returns')}
              </button>
              <button
                onClick={() => scrollToSection('api-docs')}
                className="text-left text-white text-2xl font-bold tracking-tight hover:text-[#7AC142]"
              >
                {t('nav.apiDocs')}
              </button>
              <Link
                to="/account-setup"
                onClick={() => setIsMobileMenuOpen(false)}
                className="text-left text-white text-2xl font-bold tracking-tight hover:text-[#7AC142]"
              >
                {t('nav.accountSetup')}
              </Link>
              
              {/* Ship With Us Button - Mobile */}
              <a
                href={QUOTE_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsMobileMenuOpen(false)}
                className="mt-4 bg-[#7AC142] text-black text-center py-4 text-xl font-bold uppercase tracking-wider hover:bg-[#69A835] transition-colors"
                data-testid="ship-with-us-btn-mobile"
              >
                {t('nav.shipWithUs')}
              </a>
            </div>

            {/* Mobile Language Selector */}
            <div className="p-6 border-t border-white/10">
              <div className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-white/50" />
                <div className="flex gap-1">
                  {languages.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => {
                        changeLanguage(lang.toLowerCase());
                        setIsMobileMenuOpen(false);
                      }}
                      className={`px-4 py-2 text-sm font-bold tracking-wider ${
                        language === lang.toLowerCase()
                          ? 'bg-[#7AC142] text-black'
                          : 'text-white/70 border border-white/20'
                      }`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
